import { TaskStatus, TaskView } from '@nexelem/graphne-api';
import { useDateFormatter } from '@nexelem/utils';
import { Badge, IconButton } from '@nexelem/components';

import { CUSTOM_ICONS } from '@nexelem/icons';
import { ProLightPen } from '@nexelem/icons/ProLightPen';
import { useCustomTranslation } from '../../../../../../hooks';
import { getOverdueDuration, getRetoolingDuration } from '../../../../../../utils/functions/getDuration';
import { getTimeFormat } from '../../../../../../utils/functions/getTimeFormat';
import { startedStatuses } from '../constants';
import { useGetSimulationStatus } from '../../../../../../components/organisms/ProductionPlans/GanttSimulation/hooks/useGetSimulationStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarClock, faCalendarDay, faClock, faIndustryWindows, faLock } from '@fortawesome/pro-light-svg-icons';

type ProductionTaskCardProps = {
  task: TaskView;
  isEditable: boolean;
  onEditToggle: () => void;
  isSuggested?: boolean;
};

export const ProductionTaskCard = ({ task, isEditable, onEditToggle, isSuggested }: ProductionTaskCardProps) => {
  const { start, end, dueDate, setupEnd, estimatedDurations, status, queue, releaseDate, duration } = task;
  const { tWrapper } = useCustomTranslation('products.productionPlan.taskDetailsDrawer');
  const formatDate = useDateFormatter({ displayWithHours: true });
  const { disableActions } = useGetSimulationStatus();

  const productionStart = start && formatDate(start);
  const productionEnd = end && formatDate(end);
  const overdueDuration = getOverdueDuration(dueDate, end);
  const productionDuration = duration && getTimeFormat(duration);
  const estimatedProductionDuration = getTimeFormat(estimatedDurations.median);
  const retoolingDuration = getRetoolingDuration(start, setupEnd);

  const hasStarted = startedStatuses.includes(status);
  const isUnplanned = status === TaskStatus.NotPlanned;

  if (isUnplanned) {
    return (
      <div
        className={`flex flex-col rounded-xs border border-border-container bg-surface-container-lighter ${!isEditable ? 'gap-5 pt-5' : 'gap-4 pt-2'} pb-6`}
      >
        <div className="flex items-center justify-between pr-2 pl-4">
          <span className="text-t16-b text-text-primary">{tWrapper('task')}</span>
          <div className="flex items-center gap-1">
            {isEditable && (
              <IconButton disabled={disableActions} Icon={ProLightPen} size="small" onClick={onEditToggle} />
            )}
          </div>
        </div>
        <div className="px-4">
          <div className="flex flex-col gap-1">
            <span className="py-1 text-t14-sb text-text-secondary-darker">
              {tWrapper('productionUnplannedDuration')}
            </span>
            <span className="flex items-center gap-2 text-s18-spaced text-text-primary">
              <FontAwesomeIcon className="!h-6 w-6" icon={faIndustryWindows} />
              {estimatedProductionDuration}
            </span>
          </div>
          {retoolingDuration && (
            <div className="flex flex-col gap-1">
              <span className="py-1 text-t14-sb text-text-secondary-darker">
                {tWrapper('retoolingUnplannedDuration')}
              </span>
              <span className="flex items-center gap-2 text-s18-spaced text-text-primary">
                <CUSTOM_ICONS.MachineRetool className="h-6 w-6" />
                {retoolingDuration}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col gap-3 rounded-xs border border-border-container bg-surface-container-lighter pb-6 ${!isEditable ? 'gap-5 pt-5' : 'gap-3 pt-2'}`}
    >
      <div className="flex items-center justify-between pr-2 pl-4">
        <span className="text-t16-b text-text-primary">{tWrapper('task')}</span>
        <div className="flex items-center gap-1">
          {isEditable && (
            <IconButton disabled={disableActions} Icon={ProLightPen} size="small" onClick={onEditToggle} />
          )}
        </div>
      </div>
      <div className="px-4">
        <div className="flex flex-col gap-6">
          {queue && (
            <div className="flex flex-col gap-1">
              <span className="py-1 text-t14-sb text-text-secondary-darker">{tWrapper('queue')}</span>
              <div className="flex items-center gap-2">
                <div className="!h-4 !w-4 shrink-0 rounded-full" style={{ backgroundColor: `#${queue.area.hex}` }} />
                <span className="text-s18-spaced text-text-primary">
                  <span className="font-bold break-words">{queue.name}</span> ({queue.shortName})
                </span>
                {isSuggested && <Badge variant="highlighted">{tWrapper('labelSuggested')}</Badge>}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <span className="py-1 text-t14-sb text-text-secondary-darker">
              {hasStarted ? tWrapper('productionStart') : tWrapper('estimatedStart')}{' '}
              {releaseDate.isLocked && tWrapper('blocked')}
            </span>
            <span className="flex items-center gap-2 text-s18-spaced text-text-primary">
              {releaseDate.isLocked ? (
                <FontAwesomeIcon className="!h-6 w-6" icon={faLock} />
              ) : (
                <FontAwesomeIcon className="!h-6 w-6" icon={faCalendarClock} />
              )}
              {productionStart}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="py-1 text-t14-sb text-text-secondary-darker">
              {hasStarted ? tWrapper('productionEnd') : tWrapper('estimatedEnd')}
            </span>
            <span className="flex items-center gap-2 text-s18-spaced text-text-primary">
              <FontAwesomeIcon className="!h-6 w-6" icon={faCalendarDay} />
              {productionEnd}
            </span>
          </div>

          {overdueDuration && (
            <div className="flex flex-col gap-1">
              <span className="py-1 text-t14-sb text-text-secondary-darker">{tWrapper('overdueDuration')}</span>
              <span className="flex items-center gap-2 text-s18-spaced text-text-state-error-light">
                <FontAwesomeIcon className="!h-6 w-6" icon={faClock} />
                {overdueDuration}
              </span>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <span className="py-1 text-t14-sb text-text-secondary-darker">
              {hasStarted ? tWrapper('productionDuration') : tWrapper('productionEstimatedDuration')}
            </span>
            <span className="flex items-center gap-2 text-s18-spaced text-text-primary">
              <FontAwesomeIcon className="!h-6 w-6" icon={faIndustryWindows} />
              {productionDuration}
            </span>
            {/* TODO waiting for BE team */}
            {/* <Banner
                className="mt-5 bg-transparent"
                emphasis="weak"
                FirstIcon={ProLightTriangleExclamation}
                maxWidth="100%"
                message="Duration has been edited."
                type="warning"
              /> */}
          </div>

          {retoolingDuration && (
            <div className="flex flex-col gap-1">
              <span className="py-1 text-t14-sb text-text-secondary-darker">
                {hasStarted ? tWrapper('retoolingDuration') : tWrapper('retoolingEstimatedDuration')}
              </span>
              <span className="flex items-center gap-2 text-s18-spaced text-text-primary">
                <CUSTOM_ICONS.MachineRetool className="h-6 w-6" />
                {retoolingDuration}
              </span>
            </div>
          )}

          {/* TODO waiting for BE team */}
          {/* {downtimeDuration && (
            <div className="flex flex-col gap-1">
              <span className="py-1 text-t14-sb text-text-secondary-darker">{tWrapper('downtimeDuration')}</span>
              <span className="flex items-center gap-2 text-s18-spaced text-text-primary">
                <ProLightHourglassClock className="! h-6 w-6" />
                {downtimeDuration}
              </span>
            </div>
          )} */}

          {/* TODO waiting for BE team */}
          {/* {pauseDuration && (
            <div className="flex flex-col gap-1">
              <span className="py-1 text-t14-sb text-text-secondary-darker">Pause duration</span>
              <span className="text-s18-spaced flex items-center gap-2 text-text-primary">
                <ProLightCirclePause className="!h-6 w-6" />
                10min {pauseDuration}
              </span>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};
